import { useState, useCallback } from 'react';
import confetti from 'canvas-confetti';
import useSpotDetail from '../../hooks/useSpotDetail';
import { getSpotMeta } from '../../constants/spotMeta';
import {
  getMapGoalCount,
  getRallyIdsBySpotId,
  getSpotIdsByRallyId,
  getStampCampaign,
  RALLY_META,
  MAP_GOAL_CONFIG,
} from '../../constants/mapConfig';
import { getNftCardInfo } from '../../config/externalLinks';
import ProgressBar from './ProgressBar';
import StampUserList from './StampUserList';
import LoadingSpinner from '../common/LoadingSpinner';
import InfoModal from '../common/InfoModal';

// ── ラリー別タブカラー（全国版 / 単独版で分岐） ────────────────────────
// 全国版（RALLY_META.isGlobal: true）: 「の」アイコンテーマカラー
// 単独版（isGlobal: false/未設定）  : ひろばテーマカラー
const GLOBAL_RALLY_COLOR     = '#920000';
const INDIVIDUAL_RALLY_COLOR = '#ea5521';

const getRallyTabColor = (rallyId) =>
  RALLY_META[rallyId]?.isGlobal ? GLOBAL_RALLY_COLOR : INDIVIDUAL_RALLY_COLOR;
const getRallyTabLabel = (rallyId) =>
  RALLY_META[rallyId]?.displayName ?? rallyId;

// ── 達成バッジ・未達成ドット ─────────────────────────────────────────────
const AchievedBadge = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
    <circle cx="9" cy="9" r="9" fill="#4ADE80" />
    <polyline points="5,9.5 7.5,12 13,6.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const UnachievedDot = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
    <circle cx="9" cy="9" r="5" fill="#D1D5DB" />
  </svg>
);

// ── ラリーとは？モーダル内容 ────────────────────────────────────────────
const RallyModalContent = () => (
  <div className="space-y-3 text-sm text-gray-700">
    <p>けもフレダイアリーひろば「みんなで達成！！！スタンプラリー」を現在開催中です。</p>
    <p>本企画は、ズーともの皆さんから寄せられたアイデアをもとに生まれた、「みんなで協力して目標達成を目指す」スタンプラリー企画です。</p>
    <div>
      <p className="font-bold">【スタンプの取得方法】</p>
      <p>対象の動物園・水族館を訪れ、けもフレダイアリーアプリでスタンプを受け取ってください。取得したスタンプはこのマップに反映されます。</p>
      <p>スタンプの取得はスポット情報の「入手する」「購入する」ボタンから確認してください。</p>
    </div>
    <div>
      <p className="font-bold">【無料版・有料版について】</p>
      <p>各スポットでは無料版と有料版の2種類のスタンプカードを取得できます。有料版を取得した方には 🥇 称号バッジが付与されます。</p>
    </div>
    <div>
      <p className="font-bold">【みんなでゴールを目指そう！】</p>
      <p>各スポットには目標スタンプ数が設定されています。みんなで力を合わせて目標数をクリアし、スペシャルバスツアーの実現を目指しましょう！</p>
    </div>
  </div>
);

// ── 対象スポット一覧テーブル（フィルター・ソート付き） ─────────────────────
const SpotListTable = ({ targetSpotIds, allSpots }) => {
  const [filter, setFilter] = useState('all');
  const [direction, setDirection] = useState('north');

  const FILTER_OPTIONS = [
    { value: 'all',        label: '全て' },
    { value: 'unachieved', label: '未達成' },
    { value: 'achieved',   label: '達成済み' },
  ];
  const DIRECTION_OPTIONS = [
    { value: 'north', label: '北→南' },
    { value: 'south', label: '南→北' },
  ];

  const allRows = targetSpotIds.map((spotId) => {
    const goalCount  = getMapGoalCount(spotId);
    const spot       = allSpots.find((s) => s.id === spotId);
    const stampCount = spot?.stampCount ?? 0;
    return {
      spotId,
      name:     spot?.name ?? spotId,
      goalCount,
      achieved: stampCount >= goalCount && goalCount > 0,
      lat:      spot?.lat ?? 0,
    };
  });

  const filtered = allRows.filter((row) => {
    if (filter === 'achieved')   return row.achieved;
    if (filter === 'unachieved') return !row.achieved;
    return true;
  });
  const sorted = [...filtered].sort((a, b) =>
    direction === 'north' ? b.lat - a.lat : a.lat - b.lat
  );

  return (
    <div className="mt-2">
      <div className="mb-1.5 flex items-center gap-2">
        <div className="flex overflow-hidden rounded-md border border-gray-200">
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setFilter(opt.value)}
              className={`px-2 py-0.5 text-xs transition-colors ${
                filter === opt.value ? 'bg-amber-600 text-white font-bold' : 'bg-white text-gray-500'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex overflow-hidden rounded-md border border-gray-200">
          {DIRECTION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setDirection(opt.value)}
              className={`px-2 py-0.5 text-xs transition-colors ${
                direction === opt.value ? 'bg-amber-600 text-white font-bold' : 'bg-white text-gray-500'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-hidden rounded-lg border border-gray-100">
        <div className="flex bg-gray-50 px-3 py-1.5">
          <span className="flex-1 text-xs font-bold text-gray-500">スポット名</span>
          <span className="w-24 text-right text-xs font-bold text-gray-500">目標スタンプ数</span>
        </div>
        <div className="max-h-48 overflow-y-auto">
          {sorted.length === 0 ? (
            <div className="px-3 py-3 text-center text-xs text-gray-400">該当するスポットはありません</div>
          ) : (
            sorted.map(({ spotId, name, goalCount, achieved }) => (
              <div
                key={spotId}
                className={`flex items-center border-t border-gray-100 px-3 py-2 ${achieved ? 'bg-green-50' : 'bg-white'}`}
              >
                <span className="mr-2">{achieved ? <AchievedBadge /> : <UnachievedDot />}</span>
                <span className={`flex-1 text-xs ${achieved ? 'text-green-700' : 'text-gray-700'}`}>{name}</span>
                <span className={`w-24 text-right text-xs ${achieved ? 'text-green-600' : 'text-gray-500'}`}>
                  {goalCount.toLocaleString()}スタンプ
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ── ラリー別レギュレーション内容（達成報酬・スポット一覧・達成進捗） ──────
const RallyRegulationContent = ({ rallyId, allSpots }) => {
  const [spotListOpen, setSpotListOpen] = useState(false);

  // ラリーGoalを直接参照（エリアGoal経由をやめ、ラリーごとに独立した達成条件を参照する）
  const rallyGoal     = MAP_GOAL_CONFIG.rallies?.[rallyId]?.everyoneGoal ?? null;
  const targetSpotIds = rallyGoal?.targetSpotIds ?? getSpotIdsByRallyId(rallyId);
  const reward        = rallyGoal?.reward ?? null;

  const achievedCount = targetSpotIds.filter((spotId) => {
    const goalCount = getMapGoalCount(spotId);
    const spot      = allSpots.find((s) => s.id === spotId);
    return spot && (spot.stampCount ?? 0) >= goalCount;
  }).length;

  return (
    <div className="px-4 py-3">
      {/* 達成報酬 */}
      {reward ? (
        <div>
          <p className="text-xs font-bold text-amber-700">達成報酬：</p>
          <p className="mt-0.5 whitespace-pre-line text-xs text-gray-600">
            {reward.description ?? reward.text}
          </p>
        </div>
      ) : (
        <p className="text-xs text-gray-400">達成報酬は近日公開予定</p>
      )}

      {targetSpotIds.length > 0 && (
        <>
          {/* 対象スポット一覧（アコーディオン） */}
          <div className="mt-2">
            <p className="text-xs font-bold text-amber-700">対象スポット一覧：</p>
            <p className="mt-0.5 text-xs text-gray-600">
              スポットとスタンプ数条件の一覧は
              <button
                className="ml-0.5 text-xs font-bold text-blue-600 underline"
                onClick={() => setSpotListOpen((v) => !v)}
              >
                こちら
              </button>
              <span className="ml-0.5 text-xs text-gray-400">{spotListOpen ? '▲' : '▼'}</span>
            </p>
            {spotListOpen && <SpotListTable targetSpotIds={targetSpotIds} allSpots={allSpots} />}
          </div>

          {/* 達成進捗 */}
          <div className="mt-2">
            <div className="flex items-baseline gap-1.5">
              <p className="text-xs font-bold text-amber-700">達成進捗：</p>
              <span className="text-xs text-gray-500">{achievedCount} / {targetSpotIds.length} スポット</span>
            </div>
            <div className="mt-1">
              <ProgressBar current={achievedCount} total={targetSpotIds.length} label={null} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * SpotDetailSheet - スポット詳細ボトムシート
 *
 * レイアウト（上から）:
 *   1. ラリー切り替えタブ（ニュースサイト風ソリッドタブ）+ レギュレーション内容
 *   2. スタンプカード画像・スポット名・このスポットの達成目標バー
 *   3. スタンプ取得者リスト
 */
const SpotDetailSheet = ({ spotId, onClose, useMock = false, allSpots = [] }) => {
  const { spot, stampUsers, loading, error } = useSpotDetail(spotId, useMock);
  const staticMeta = getSpotMeta(spotId);
  const meta = {
    thumbnailUrls:
      staticMeta.thumbnailUrls.length > 0
        ? staticMeta.thumbnailUrls
        : (spot?.thumbnailUrls ?? []),
    address: staticMeta.address || spot?.address || '',
  };

  const rallyIds = getRallyIdsBySpotId(spotId);
  const [activeRallyId, setActiveRallyId] = useState(rallyIds[0] ?? null);
  const [rallyModalOpen, setRallyModalOpen] = useState(false);
  const [zoomUrl, setZoomUrl] = useState(null);

  const configGoalCount = getMapGoalCount(spotId);
  const goalCount   = configGoalCount > 0 ? configGoalCount : 250;
  const currentCount = spot ? (spot.stampCount ?? 0) : 0;

  console.debug(`[SpotDetailSheet] 📊 状態: spotId=${spotId}, loading=${loading}, error=${error?.message || 'none'}, spot=${spot?.name || 'null'}`);

  const handleCelebration = useCallback(() => {
    confetti({
      particleCount: 80, angle: 60, spread: 55,
      origin: { x: 0, y: 1 },
      colors: ['#FBBF24', '#10B981', '#3B82F6', '#EF4444', '#8B5CF6'],
      zIndex: 9999,
    });
    confetti({
      particleCount: 80, angle: 120, spread: 55,
      origin: { x: 1, y: 1 },
      colors: ['#FBBF24', '#10B981', '#3B82F6', '#EF4444', '#8B5CF6'],
      zIndex: 9999,
    });
  }, []);

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-lp">
      {/* 画像拡大モーダル */}
      {zoomUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-6"
          onClick={() => setZoomUrl(null)}
        >
          <img
            src={zoomUrl}
            alt="拡大表示"
            className="max-h-full max-w-full rounded-xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/40"
            onClick={() => setZoomUrl(null)}
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>
      )}

      {/* 背景オーバーレイ */}
      <div className="fixed inset-0 bg-black/30" onClick={onClose} aria-hidden="true" />

      {/* シート本体 */}
      <div className="relative z-10 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl">

        {/* ── スティッキーヘッダー（ドラッグハンドル + 閉じるボタン） ── */}
        <div className="sticky top-0 z-20 bg-white pb-2 pt-3">
          <div className="flex justify-center">
            <div className="h-1 w-10 rounded-full bg-gray-300" />
          </div>
          <button
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"
            onClick={onClose}
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="mb-2 text-sm font-bold text-red-600">⚠️ データ取得エラー</p>
            <p className="mb-4 text-xs text-red-500">{error.message}</p>
            {process.env.NODE_ENV === 'development' && (
              <div className="mb-4 rounded bg-red-50 p-2 text-left">
                <p className="text-[10px] font-mono text-red-600">
                  spotId: {spotId}
                  <br />
                  error.code: {error.code || 'unknown'}
                  <br />
                  timestamp: {new Date().toISOString()}
                </p>
              </div>
            )}
            <button
              onClick={() => window.location.reload()}
              className="inline-block rounded bg-red-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-600"
            >
              再試行
            </button>
          </div>
        ) : (
          <>
            {/* ── セクション1: ラリーレギュレーション ── */}
            {rallyIds.length > 0 && activeRallyId && (
              <section>
                <div className='mt-5'></div>
                {/* セクションヘッダー行（スタンプとは？リンク付き） */}
                <div className="flex items-center justify-between bg-amber-50 px-4 py-1.5">
                  <span className="text-xs font-bold text-amber-800">ラリー情報</span>
                  <button
                    className="text-xs font-bold text-blue-600 underline"
                    onClick={() => setRallyModalOpen(true)}
                  >
                    スタンプラリーとは？
                  </button>
                </div>

                {/* ニュースサイト風ソリッドタブバー */}
                <div className="flex">
                  {rallyIds.map((rallyId) => {
                    const isActive = activeRallyId === rallyId;
                    const color    = getRallyTabColor(rallyId);
                    return (
                      <button
                        key={rallyId}
                        onClick={() => setActiveRallyId(rallyId)}
                        className="flex-1 py-2.5 text-[10px] font-bold text-white transition-opacity border-[4px] "
                        style={{ backgroundColor: color, opacity: isActive ? 1 : 0.45 }}
                      >
                        {getRallyTabLabel(rallyId)}
                      </button>
                    );
                  })}
                </div>

                {/* 選択中ラリーのレギュレーション */}
                <RallyRegulationContent rallyId={activeRallyId} allSpots={allSpots} />

                {/* セクション区切り */}
                <div className="h-2 bg-gray-50" />
              </section>
            )}

            {/* ── セクション2: スポット詳細 ── */}
            <div className="flex items-center justify-between bg-amber-50 px-4 py-1.5">
                  <span className="text-xs font-bold text-amber-800"> スタンプ情報</span>
                  {/* <button
                    className="text-xs font-bold text-blue-600 underline"
                    onClick={() => setStampModalOpen(true)}
                  >
                    スタンプとは？
                  </button> */}
                </div>
            <section className="space-y-4 px-4 pb-4 pt-3">
              {/* スタンプ倍数キャンペーンバナー */}
              {(() => {
                const campaign = getStampCampaign(spotId);
                if (!campaign) return null;
                const fmt = (d) => d.replace(/(\d{4})-(\d{2})-(\d{2})/, '$1/$2/$3');
                return (
                  <div
                    className="rounded-xl px-4 py-3 shadow-md space-y-2"
                    style={{ background: 'linear-gradient(135deg, #f59e0b, #ef4444)' }}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-xl">🎉</span>
                      <span className="text-base font-black text-white tracking-wide drop-shadow">
                        スタンプ{campaign.multiplier}倍キャンペーン開催中！
                      </span>
                      <span className="text-xl">🎉</span>
                    </div>
                    <p className="text-xs text-white/90 leading-relaxed">
                      {fmt(campaign.startDate)}〜{fmt(campaign.endDate)}の期間中、このスポットでスポットカードを入手すると、集まるスタンプが通常の{campaign.multiplier}倍になります。「日本全国動物園・水族館スポットラリー 東日本」の目標スタンプ達成にぐっと近づきましょう！
                    </p>
                  </div>
                );
              })()}

              {/* スタンプカード画像（最大2枚） */}
              {meta.thumbnailUrls.length > 0 ? (
                <div className="flex gap-3">
                  {meta.thumbnailUrls.slice(0, 2).map((url, i) => {
                    const cardType    = i === 0 ? 'free' : 'paid';
                    const cardInfo    = getNftCardInfo(spotId, cardType);
                    return (
                      <div key={i} className="w-[40%] flex-shrink-0">
                        <span className={`mb-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold text-white ${i === 0 ? 'bg-amber-400' : 'bg-green-500'}`}>
                          {i === 0 ? '無料版スタンプカード' : '有料版スタンプカード'}
                        </span>
                        <div className="rounded-xl bg-gray-100">
                          <img
                            src={url}
                            alt={`${spot?.name || 'スポット'} ${i === 0 ? '無料版' : '有料版'}`}
                            className="aspect-square w-full cursor-pointer rounded-xl object-contain shadow-sm transition-opacity active:opacity-70"
                            onClick={() => setZoomUrl(url)}
                            onError={(e) => { e.target.parentElement.parentElement.style.display = 'none'; }}
                          />
                        </div>
                        {cardInfo && (
                          <a
                            href={cardInfo.isAvailable ? cardInfo.url : undefined}
                            target={cardInfo.isAvailable ? '_blank' : undefined}
                            rel={cardInfo.isAvailable ? 'noopener noreferrer' : undefined}
                            onClick={(e) => {
                              if (!cardInfo.isAvailable) e.preventDefault();
                            }}
                            className={`mt-1.5 flex w-full items-center justify-center gap-1 rounded-lg py-1.5 text-[11px] font-bold text-white transition-opacity active:opacity-75 ${
                              i === 0 ? 'bg-amber-400' : 'bg-green-500'
                            } ${!cardInfo.isAvailable ? 'cursor-not-allowed opacity-60' : ''}`}
                          >
                            {cardInfo.label}
                            {cardInfo.isAvailable && (
                              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                                <path d="M1.5 8.5L8.5 1.5M8.5 1.5H4M8.5 1.5V6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex w-[40%] items-center justify-center rounded-xl bg-gray-200 py-8 text-3xl">
                  📍
                </div>
              )}

              {/* スポット名 */}
              <h2 className="text-lg font-bold text-gray-900">
                {spot?.name || '読み込み中...'}
              </h2>

              {/* このスポットの達成目標バー */}
              {currentCount >= goalCount ? (
                <div className="rounded-lg bg-green-50 p-3">
                  <p className="mb-1 text-xs font-bold text-green-700">このスポットの達成目標</p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleCelebration}
                      className="relative flex flex-1 items-center justify-center gap-1.5 rounded-full bg-green-500 py-2 transition-all duration-150 hover:bg-green-400 hover:shadow-lg hover:shadow-green-300/60 active:scale-95 active:bg-green-600"
                      aria-label="達成！タップして祝おう"
                    >
                      <span className="text-sm font-bold text-white">達成！</span>
                    </button>
                    <span className="flex-shrink-0 text-xs font-bold text-green-700">
                      {currentCount} / {goalCount}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-green-600">目標スタンプ数を達成しました！みんなありがとう！</p>
                  <p className="mt-1 text-[10px] text-green-600">「達成！」を押してお祝いしよう</p>
                </div>
              ) : (
                <div className="rounded-lg bg-amber-50 p-3">
                  <p className="mb-1 text-xs font-bold text-amber-700">このスポットの達成目標</p>
                  <ProgressBar current={currentCount} total={goalCount} />
                </div>
              )}
            </section>

            {/* ── セクション3: スタンプ取得者リスト ── */}
            <section className="px-4 pb-8">
              <StampUserList users={stampUsers} />
            </section>
          </>
        )}
      </div>

      {/* ラリーとは？モーダル */}
      <InfoModal isOpen={rallyModalOpen} onClose={() => setRallyModalOpen(false)} title="スタンプラリーとは？">
        <RallyModalContent />
      </InfoModal>
    </div>
  );
};

export default SpotDetailSheet;
